import * as yup from 'yup';
import {
	EMPTY_ARRAY,
	Row,
	Column,
	RenderConfig,
	TextInput,
	SelectInput,
	BOOLEAN_OPTIONS,
	DEVICE_TYPES,
	UPDATE_TYPES,
	AsyncSelect
} from 'commons-ui';
import _get from 'lodash/get';
import { APP_LANGUAGE } from 'commons/models/common';

export const FORM_FIELD_KEYS = {
	VERSION_NUMBER: 'targetVersionCode',
	IS_AVAILABLE_ON_PLAY_STORE: 'isAvailableOnPlayStore',
	IS_FORCE_UPDATE: 'isForceUpdate',
	DESCRIPTIONS: 'descriptions',
	DESCRIPTION_ENG: 'descEn',
	DESCRIPTION_HIN: 'descHi',
	MAX_VERSION: 'maxVersion',
	MIN_VERSION: 'minVersion',
	FORCE_BEFORE_LOGIN: 'forceBeforeLogin',
	LUDO_SUPREME_APP_UPDATE_URL: 'ludoSupremeAppUpdateUrl',
	DEVICE_TYPE: 'deviceType',
	UPDATE_TYPE: 'updateType',
	IS_SEGMENT_WISE_UPDATE_REQ: 'isSegmentWiseUpdateReq',
	SEGMENTS: 'segments',
	LUDO_APP_APK_URL: 'ludoAppApkUrl',
	AB_TEST_ON: 'isABTestOn'
};

const deviceTypeDependencyResolver = (props: any, dependencyValues: any) => {
	const selectedReason = _get(dependencyValues, '0');
	const { disabled } = props;
	if (selectedReason === 'ios' && !disabled) {
		return { disabled: true, required: false };
	}
	return null;
};

export const validationSchema = yup.object().shape({
	targetVersionCode: yup.number(),
	minVersion: yup.number().required('Min Version is a required field'),
	maxVersion: yup
		.number()
		.required('Max Version is a required field')
		.when(['minVersion'], (minVersion, schema) => {
			if (minVersion) {
				return yup
					.number()
					.min(minVersion, 'max version should be greater than min version')
					.typeError('max version is required');
			}
			return schema;
		}),
	isForceUpdate: yup.boolean().required('Is Force Update a required field'),
	isSegmentWiseUpdate: yup.boolean().required(),
	segments: yup.string().when('isSegmentWiseUpdate', {
		is: true,
		then: yup.string().required(),
		otherwise: yup.string().optional()
	}),
	updateType: yup.string().when('isSegmentWiseUpdate', {
		is: true,
		then: yup.string().required(),
		otherwise: yup.string().optional()
	}),
	deviceType: yup.string().required('Device Type is required'),
	descriptions: yup
		.object()
		.shape({
			[APP_LANGUAGE.ENGLISH]: yup.string().required('It is a required field'),
			[APP_LANGUAGE.HINDI]: yup.string().required('It is a required field')
		})
		.required(),
	forceBeforeLogin: yup.boolean().required('Force before login is a required field'),
	isABTestOn: yup.boolean().required('ABTest On is a required field')
});

export const FORM_FIELD_CONFIG = {
	[FORM_FIELD_KEYS.VERSION_NUMBER]: new RenderConfig()
		.setName('targetVersionCode')
		.setLabel('Update to this version number')
		.setRenderer(AsyncSelect)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			dependencies: ['deviceType'],
			dependencyResolver: deviceTypeDependencyResolver
		}),
	[FORM_FIELD_KEYS.IS_AVAILABLE_ON_PLAY_STORE]: new RenderConfig()
		.setName('isAvailableOnPlayStore')
		.setLabel('Is Available On PlayStore')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: false, edit: false },
			isHidden: { create: true, edit: true }, // hiding because only reqd by backend, not frontend
			inputType: 'boolean',
			required: true,
			options: BOOLEAN_OPTIONS
		}),
	[FORM_FIELD_KEYS.IS_FORCE_UPDATE]: new RenderConfig()
		.setName('isForceUpdate')
		.setLabel('Is Force Update')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'boolean',
			required: true,
			options: BOOLEAN_OPTIONS
		}),
	[FORM_FIELD_KEYS.AB_TEST_ON]: new RenderConfig()
		.setName('isABTestOn')
		.setLabel('AB Test On')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'boolean',
			required: true,
			options: BOOLEAN_OPTIONS
		}),
	[FORM_FIELD_KEYS.MAX_VERSION]: new RenderConfig()
		.setName('maxVersion')
		.setLabel('Max Version')
		.setRenderer(AsyncSelect)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.MIN_VERSION]: new RenderConfig()
		.setName('minVersion')
		.setLabel('Min Version')
		.setRenderer(AsyncSelect)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true
		}),
	[FORM_FIELD_KEYS.DEVICE_TYPE]: new RenderConfig()
		.setName('deviceType')
		.setLabel('Device Type')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			options: DEVICE_TYPES
		}),
	[FORM_FIELD_KEYS.IS_SEGMENT_WISE_UPDATE_REQ]: new RenderConfig()
		.setName('isSegmentWiseUpdate')
		.setLabel('Is Segment Wise Update')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'boolean',
			required: true,
			options: BOOLEAN_OPTIONS
		}),
	[FORM_FIELD_KEYS.UPDATE_TYPE]: new RenderConfig()
		.setName('updateType')
		.setLabel('updateType')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			options: UPDATE_TYPES
		}),
	[FORM_FIELD_KEYS.SEGMENTS]: new RenderConfig()
		.setName('segments')
		.setLabel('Segments(comma separated)')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: false,
			inputType: 'string'
		}),
	[FORM_FIELD_KEYS.FORCE_BEFORE_LOGIN]: new RenderConfig()
		.setName('forceBeforeLogin')
		.setLabel('Force Before Login')
		.setRenderer(SelectInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'boolean',
			required: true,
			options: BOOLEAN_OPTIONS
		}),
	[FORM_FIELD_KEYS.DESCRIPTIONS]: new RenderConfig()
		.setName('descriptions')
		.setLabel('Description')
		.setRenderProps({
			isEditable: { create: true, edit: true },
			inputType: 'string'
		}),
	[FORM_FIELD_KEYS.DESCRIPTION_ENG]: new RenderConfig()
		.setName(APP_LANGUAGE.ENGLISH)
		.setLabel('English')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			inputType: 'string'
		}),
	[FORM_FIELD_KEYS.DESCRIPTION_HIN]: new RenderConfig()
		.setName(APP_LANGUAGE.HINDI)
		.setLabel('Hindi')
		.setRenderer(TextInput)
		.setRenderProps({
			isEditable: { create: true, edit: true },
			required: true,
			inputType: 'string'
		}),
	[FORM_FIELD_KEYS.LUDO_APP_APK_URL]: new RenderConfig()
		.setName('ludoAppApkUrl')
		.setLabel('Apk url for updated app')
		.setRenderer(TextInput)
		.setRenderProps({
			isHidden: { create: true, edit: true },
			isEditable: { create: false, edit: false },
			inputType: 'string',
			required: true
		}),
	[FORM_FIELD_KEYS.LUDO_SUPREME_APP_UPDATE_URL]: new RenderConfig()
		.setName('ludoSupremeAppUpdateUrl')
		.setLabel('Website to download the app')
		.setRenderer(TextInput)
		.setRenderProps({
			isHidden: { create: true, edit: true }, // hiding because only reqd by backend, not frontend
			isEditable: { create: false, edit: false },
			inputType: 'string',
			required: true
		})
};

export const FORM_LAYOUT_CONFIG = [
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.DEVICE_TYPE)])]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.VERSION_NUMBER)]),
		new Column([new Row(FORM_FIELD_KEYS.MIN_VERSION)]),
		new Column([new Row(FORM_FIELD_KEYS.MAX_VERSION)])
	]),
	new Row(FORM_FIELD_KEYS.DESCRIPTIONS, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.DESCRIPTION_ENG)]),
		new Column([new Row(FORM_FIELD_KEYS.DESCRIPTION_HIN)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.FORCE_BEFORE_LOGIN)]),
		new Column([new Row(FORM_FIELD_KEYS.IS_FORCE_UPDATE)]),
		new Column([new Row(FORM_FIELD_KEYS.AB_TEST_ON)])
	]),
	new Row(null, EMPTY_ARRAY, [
		new Column([new Row(FORM_FIELD_KEYS.IS_SEGMENT_WISE_UPDATE_REQ)]),
		new Column([new Row(FORM_FIELD_KEYS.SEGMENTS)]),
		new Column([new Row(FORM_FIELD_KEYS.UPDATE_TYPE)])
	]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.LUDO_APP_APK_URL)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.IS_AVAILABLE_ON_PLAY_STORE)])]),
	new Row(null, EMPTY_ARRAY, [new Column([new Row(FORM_FIELD_KEYS.LUDO_SUPREME_APP_UPDATE_URL)])])
];

export const getFormDefaults = (websiteURL: string) => {
	return {
		forceBeforeLogin: false,
		deviceType: 'android',
		isABTestOn: false,
		isAvailableOnPlayStore: false,
		ludoSupremeAppUpdateUrl: websiteURL,
		descriptions: {
			[APP_LANGUAGE.HINDI]: 'उदाहरण'
		}
	};
};
